import api from "../api";

const guardianDashboardService = {
  // Get guardian dashboard data
  getDashboardData: async () => {
    try {
      const response = await api.get("/guardian-dashboard");
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch dashboard data"
      );
    }
  },

  // Submit payment confirmation (matching Laravel property-application.payment.submit)
  submitPayment: async (rentId) => {
    try {
      const response = await api.post(
        `/guardian-payments/property-application/payment/${rentId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to submit payment"
      );
    }
  },

  // Submit recommend a friend form
  recommendFriend: async (formData) => {
    try {
      const response = await api.post(
        "/guardian-dashboard/recommend-friend",
        formData
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to submit recommendation"
      );
    }
  },

  // Get recommend friend thank you page data
  getRecommendFriendThanks: async () => {
    try {
      const response = await api.get(
        "/guardian-dashboard/recommend-friend-thanks"
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to load thank you page"
      );
    }
  },

  // Get available properties
  getAvailableProperties: async () => {
    try {
      const response = await api.get("/guardian-property-search");
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch properties"
      );
    }
  },

  // Get scheduled viewings
  getScheduledViewings: async () => {
    try {
      const response = await api.get("/guardian-viewings");
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch viewings"
      );
    }
  },

  // Update newsletter subscription
  updateNewsletter: async (checked, guardianId) => {
    try {
      const response = await api.post("/guardians/change-field", {
        guardianId: guardianId,
        field: "on_newsletter_list",
        value: checked,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Failed to update newsletter preference"
      );
    }
  },

  // Update voluntary work preference
  updateVoluntaryWork: async (checked, guardianId) => {
    try {
      const response = await api.post("/guardians/change-field", {
        guardianId: guardianId,
        field: "on_voluntary_work_list",
        value: checked,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Failed to update voluntary work preference"
      );
    }
  },

  // Upload document (matching Laravel document upload logic)
  uploadDocument: async (formData, onProgress) => {
    try {
      const response = await api.post(
        "/guardians/stage-1-documents/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(progress);
            }
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to upload document"
      );
    }
  },

  // Save stage 1 data (matching Laravel stage1Data structure)
  saveStage1Data: async (stage1Data) => {
    try {
      const response = await api.post(
        "/guardians/stage-1-complete",
        stage1Data
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to save stage 1 data"
      );
    }
  },

  // Update guardian status (matching Laravel status transitions)
  updateGuardianStatus: async (newStatusId) => {
    try {
      const response = await api.post("/guardians/update-status", {
        status_id: newStatusId,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to update guardian status"
      );
    }
  },

  // Get stage 1 requirements (matching Laravel requirements structure)
  getStageOneRequirements: async () => {
    try {
      const res = await api.get("/guardians/stage-1-documents/requirements");
      return res.data;
    } catch (error) {
      console.error("Error getting Stage 1 requirements:", error);
      throw new Error(
        error.response?.data?.message || "Failed to load requirements"
      );
    }
  },

  submitStageOneDocuments: async (documents) => {
    try {
      const res = await api.post("/guardians/stage-1-documents/submit", {
        documents,
      });
      return res.data;
    } catch (error) {
      console.error("Error submitting Stage 1 documents:", error);
      throw new Error(
        error.response?.data?.message || "Failed to submit documents"
      );
    }
  },

  // Submit complete Stage 1 data including documents and personal details
  submitStageOneComplete: async ({
    guardianId,
    documents,
    personalDetails,
  }) => {
    try {
      const res = await api.post("/guardians/stage-1-complete", {
        guardianId,
        documents,
        personalDetails,
      });
      return res.data;
    } catch (error) {
      console.error("Error submitting complete Stage 1 data:", error);
      throw new Error(
        error.response?.data?.message || "Failed to submit Stage 1 data"
      );
    }
  },

  // ✅ NEW STAGE 3 METHODS - Laravel-compatible license agreement and signature endpoints

  // Get license agreement for display/download
  getLicenseAgreement: async (rentId, type = "license_agreement") => {
    try {
      const response = await api.get(
        `/guardian-dashboard/license-agreement/${rentId}/${type}`,
        { responseType: "blob" } // For PDF download
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to load license agreement"
      );
    }
  },

  // Get license agreement display URL (for iframe)
  getLicenseAgreementDisplayUrl: (rentId, type = "license_agreement") => {
    // Return URL for iframe display
    return `/api/guardian-dashboard/license-agreement/display/${rentId}/${type}`;
  },

  // Submit license agreement signature (matching Laravel licence_agreements.sign route)
  submitLicenseSignature: async (rentId, signatureData) => {
    try {
      const response = await api.post(
        `/guardian-dashboard/license-agreement/sign/${rentId}`,
        {
          guardian_sign: signatureData.signature,
          licence_date_signed: signatureData.dateSigned,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to submit license signature"
      );
    }
  },

  // ✅ NEW: Nuapay Open Banking Payment Integration
  initiateNuapayPayment: async (rentId) => {
    try {
      const response = await api.post("/payments/initiate", {
        rentId: rentId,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to initiate payment"
      );
    }
  },

  // Enhanced payment submission with Laravel-compatible structure
  submitPaymentConfirmation: async (rentId) => {
    try {
      const response = await api.post(
        `/guardian-dashboard/payment/submit/${rentId}`,
        {} // Laravel expects empty body for payment confirmation
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to submit payment confirmation"
      );
    }
  },

  // Get payment transaction status
  getPaymentStatus: async (transactionId) => {
    try {
      const response = await api.get(`/payments/transaction/${transactionId}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to get payment status"
      );
    }
  },

  // Get rent details for payment display (matching Laravel rent data structure)
  getRentDetails: async (rentId) => {
    try {
      const response = await api.get(`/rents/${rentId}/details`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to load rent details"
      );
    }
  },

  // Submit key collection ticket (matching Laravel tickets.store for key collection)
  // Laravel: final-message.blade.php submits to route('tickets.store') with:
  // - issue_with: 'room', type_id: '47', urgency_id: '3', description: user input
  submitKeyCollectionTicket: async (ticketData) => {
    try {
      const response = await api.post("/tickets", {
        issue_with: "room",
        type_id: 47, // Key Collection ticket type in Laravel
        urgency_id: 3,
        description: ticketData.description,
        property_id: ticketData.propertyId,
        room_id: ticketData.roomId,
        ticketable_type: "App\\Rooms\\Room",
        ticketable_id: ticketData.roomId,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to submit key collection request"
      );
    }
  },
};

export default guardianDashboardService;
